/**
 * Starter template: Dental appointment booking (voice).
 *
 * Same shape as the LLM intent parser's WizardConfig response, so the
 * wizard renders both paths identically.
 */
const dentalBookingTemplate = {
  channel: "voice",
  business_summary:
    "A dental practice taking appointment-booking calls from existing and new patients.",
  industry_tag: "Healthcare / Dental",
  goal: {
    primary: "Book an appointment",
    summary:
      "Greet the caller, qualify the visit type, capture name + phone, and book a slot on the calendar.",
  },
  fields: [
    {
      key: "practice_name",
      label: "Practice name",
      type: "text",
      value: null,
      source: "missing",
      required: true,
      placeholder: "e.g. Acme Dental",
      can_skip: true,
      skip_fallback: "our practice",
    },
    {
      key: "hours",
      label: "Hours of operation",
      type: "textarea",
      value: "Mon–Fri 9:00 to 17:00",
      source: "default",
      placeholder: "Mon–Fri 9 to 5",
    },
    {
      key: "services",
      label: "Services you offer",
      type: "multiselect",
      options: [
        "Cleanings",
        "Fillings",
        "Crowns",
        "Root canals",
        "Orthodontics",
        "Whitening",
        "Implants",
        "Emergency",
      ],
      value: ["Cleanings", "Fillings"],
      source: "default",
    },
    {
      key: "insurance",
      label: "Insurance accepted",
      type: "textarea",
      value: null,
      source: "missing",
      placeholder: "Delta Dental, Cigna, ...",
    },
    {
      key: "booking_window_days",
      label: "How many days out can patients book?",
      type: "number",
      value: 30,
      source: "default",
    },
  ],
  recommended_tools: [
    {
      tool_key: "google_calendar",
      reason: "Find open slots and book the appointment during the call.",
    },
  ],
  recommended_contact_syncs: [
    {
      tool_key: "crm_picker",
      fields_to_be_synced: {
        first_name: "first name given by the caller",
        last_name: "last name given by the caller",
        phone: "phone number captured",
        email: "email captured (if any)",
        visit_type: "type of visit they're booking",
      },
      reason: "Save the caller as a contact in your CRM after the call.",
    },
  ],
  recommended_conversation_syncs: [],
  recommended_outcomes: [
    {
      key: "book_calendar",
      label: "Book to my calendar during the call",
      default_on: true,
      mechanism: "tool",
      tool_key: "google_calendar",
    },
    {
      key: "contact_to_crm",
      label: "Save the caller as a contact in my CRM",
      default_on: true,
      mechanism: "contact_sync",
      tool_key: "crm_picker",
    },
    {
      key: "transcript_to_crm",
      label: "Attach the call transcript to the CRM contact",
      default_on: false,
      mechanism: "conversation_sync",
      tool_key: "crm_picker",
    },
    {
      key: "summary_email",
      label: "Email me a summary after each call",
      default_on: false,
      mechanism: "summary_email",
    },
  ],
  voice_defaults: {
    first_sentence:
      "Hi, thanks for calling {{practice_name}} — how can I help you today?",
    speaks_first: "bot",
    detect_voicemail: "hangup",
    voicemail_message:
      "We're sorry we missed you — please leave your name and number and we'll call you back.",
    max_call_duration: 600,
    use_turn_detector: true,
    listen_silence: 30,
    listen_silence_message: "Are you still there?",
  },
  behavior_defaults: {
    llm_temperature: 0.3,
    has_human_agent: false,
    use_tools: true,
    summarize: null,
  },
  prompt_skeleton: `You are the appointment-booking receptionist for {{practice_name}}, a dental practice.

Operating context:
- Hours: {{hours}}
- Services we offer: {{services|comma-list}}
- Insurance accepted: {{insurance}}
- We can book up to {{booking_window_days}} days out.

Conversation flow:
1. Opening — greet the caller warmly and ask if they're calling to book an appointment.
2. Visit type — ask which service they need from our list. If they describe symptoms ("toothache", "chip"), map to the closest service and confirm: "Sounds like you'll want a [service] — does that sound right?"
3. New vs returning — ask if they've been seen here before. If new, gather a bit more about the issue so we can budget the right slot length (15 min for cleaning, 30 min for fillings, 60 min for crowns / orthodontics consultations).
4. Capture identity — ask for their full name, then phone number. MIRROR the phone number back digit-by-digit and confirm before moving on. If new patient, also capture email and date of birth.
5. Insurance — if their visit type is covered, ask their insurance carrier and member ID. If we don't accept it, politely explain and offer the self-pay rate range.
6. Find a slot — use the google_calendar tool to find open slots that match the visit length in the next {{booking_window_days}} days. Offer up to THREE options spaced across mornings / afternoons / different days.
   - If the tool returns no slots: apologise, offer the next two earliest options, and ask if they'd like to be added to a cancellation waitlist.
   - If the tool errors: take a callback request with name + phone + preferred days and tell them we'll call within one business hour.
7. Confirm — read the booking back in full: date, day of week, time, provider (if known), visit type. Get an explicit "yes that works" before booking.
8. Close — tell them they'll receive a calendar invite by email, mention any pre-visit instructions (no eating an hour before for sedation, bring insurance card, arrive 10 min early for new-patient paperwork), and ask if there's anything else.

Turn-taking rules:
- Voice: 1-2 sentence turns. Never stack questions. Wait for them to finish before responding.
- For any digits (phone, DOB, member ID), repeat back digit-by-digit and confirm.

Tone:
Warm, unrushed, professional. Many callers are anxious about dental visits — meet them with calm. Speak in clear short sentences. Never use jargon ("occlusal", "endodontic") without translating.

Boundaries — never:
- Quote a specific price for a procedure without checking with the office.
- Give clinical advice ("That sounds like it needs a root canal" — instead say "Dr. [Name] will assess that during the visit").
- Diagnose. Even if the symptoms sound textbook, defer to the dentist.
- Promise an appointment slot the calendar didn't actually offer.

Fallbacks:
If asked about something you don't know (new procedures, specific provider availability, billing details): say honestly "Let me have someone from the office call you back about that — what's the best number?" Capture the question and move on.

Escalation:
If the caller is in acute pain or describes an emergency (uncontrolled bleeding, severe swelling, tooth knocked out): tell them to call 911 or go to the ER for a true emergency, otherwise offer to text our after-hours line and book the soonest available slot today.`,
  confidence: 1.0,
  missing_critical_fields: ["practice_name"],
};

export default dentalBookingTemplate;
